import { useState } from 'react'
import PropTypes from 'prop-types'
import { withLocalize } from 'react-localize-redux'
import cx from 'classnames'
import { useForm } from 'react-hook-form'

import { TextField } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { blue } from '@material-ui/core/colors'

import styles from './NewGroupForm.module.scss'
import _ from 'lodash'
import Typography from '@material-ui/core/Typography'
import SearchOutlinedIcon from '@material-ui/icons/SearchOutlined'

import { DeviceSelector } from 'modules/modals/NewModals/Components/Molecules/DevicesSelector/DevicesSelector'
import { InfoPopover } from 'common/components/InfoPopover/InfoPopover'
import Button from 'common/components/Button/Button'

const useStyles = makeStyles(() => ({
  input: {
    [`& fieldset`]: {
      borderRadius: 30,
      borderColor: 'grey',
      verticalAlign: 'middle'
    },
    '& label': {
      padding: 0,
      verticalAlign: 'middle'
    },
    '& label.Mui-focused': {
      color: blue[600]
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: 'grey'
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: 'grey'
      },
      '&:hover fieldset': {
        borderColor: 'grey'
      },
      '&.Mui-focused fieldset': {
        borderColor: blue[600]
      }
    }
  }
}))

const NewGroupForm = props => {
  const classes = useStyles()

  const {
    translate,
    onSubmit,
    systems,
    onCancel,
    isSubmitting,
    projects,
    redirectToManageDevices
  } = props
  const [groupName, setGroupName] = useState('')
  const [filterExpression, setFilterExpression] = useState('')
  const [selectedCheckboxIds, setSelectedCheckboxIds] = useState([])

  const { handleSubmit, register, errors, getValues, setError, clearErrors } = useForm()

  const handleFilterExpressionChange = event => {
    setFilterExpression(event.target.value)
  }

  const validateDevicesSelected = devicesSelected => {
    if (devicesSelected) {
      clearErrors('selectedDevices')
    } else {
      setError('selectedDevices', { shouldFocus: 'selectedDevices' })
    }
  }

  const checkGroupName = groupName => {
    setGroupName(groupName)
    if (groupName === '' || groupName === undefined) {
      setError('groupName', { shouldFocus: 'groupName' })
    } else {
      clearErrors('groupName')
    }
  }
  const submitFormData = () => {
    const values = getValues({ nest: true })
    checkGroupName(values.groupName)
    validateDevicesSelected(selectedCheckboxIds.length > 0)

    const allSystemsDeviceIds = systems.map(system => system._id)

    const devicesToAdd = allSystemsDeviceIds.filter(deviceId =>
      selectedCheckboxIds.includes(deviceId)
    )

    if (_.isEmpty(errors)) {
      const data = {
        group: values.groupName,
        deviceIds: devicesToAdd,
        projectId: projects.toJS().ProjectId
      }
      onSubmit(data)
    }
  }

  let filteredSystems = systems.filter(s =>
    s.name.toLowerCase().includes(filterExpression.toLowerCase())
  )

  return (
    <div className={styles.modalContainer}>
      <Typography className={styles.header}>{translate('newGroup')}</Typography>
      <div className={styles.modal}>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          onKeyDown={ev => {
            if (ev.key === 'Enter') {
              ev.preventDefault()
            }
          }}
        >
          <TextField
            variant='outlined'
            required
            fullWidth
            id='groupName'
            label={translate('groupName')}
            onChange={event => checkGroupName(event.target.value)}
            inputRef={register({ required: true })}
            name='groupName'
            value={groupName}
            helperText={`${groupName.length}/36`}
            inputProps={{ maxLength: 36 }}
            className={cx(styles.input, classes.input, {
              [styles.hasError]: errors.name
            })}
          />
          <div className={styles.wrapperError}>
            {errors.groupName && (
              <div className={styles.error}>
                <small>{translate('validation.required')}</small>
              </div>
            )}
          </div>
          <div className={styles.searchDeviceInput}>
            <SearchOutlinedIcon />
            <input
              type='text'
              id='searchDevice'
              name='searchDevice'
              placeholder={translate('searchDevice')}
              onChange={handleFilterExpressionChange}
              value={filterExpression}
            />
          </div>
          <div
            className={styles.devicesList}
            id='devicesList'
          >
            <DeviceSelector
              systems={filteredSystems}
              selectedCheckboxIds={selectedCheckboxIds}
              setSelectedCheckboxIds={setSelectedCheckboxIds}
              translate={translate}
              userWritting={filterExpression.length > 0}
              selectAllLabel={translate('newGroupForm.selectDevices')}
              selectAllInfoButton={
                <InfoPopover
                  title={translate('availableDevices')}
                  message={translate('availableDevicesInfo')}
                  linkAction={redirectToManageDevices}
                  linkText={translate('manageDevices')}
                />
              }
            />
          </div>
          <div className={styles.wrapperError}>
            {errors.selectedDevices && (
              <div className={styles.error}>
                <small>{translate('validation.anyDeviceRequired')}</small>
              </div>
            )}
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              isLoading={isSubmitting}
              type='next'
              label={translate('addGroup')}
              onClick={submitFormData}
            />
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              isLoading={isSubmitting}
              type='cancel'
              label={translate('cancel')}
              onClick={onCancel}
            />
          </div>
        </form>
      </div>
    </div>
  )
}

NewGroupForm.propTypes = {
  translate: PropTypes.func.isRequired
}

export default withLocalize(NewGroupForm)
